import React, { useEffect, useRef, useState, useCallback } from 'react';
import { facts } from '../data/facts';
import { findPath } from '../utils/pathfinding';
import gameSounds from '../utils/sounds';
import ProgressBar from './ProgressBar';
import MobileDPad from './MobileDPad';
import '../styles/SnakeGame.css';

const SnakeGameAutopilot = ({ onFoodEaten, currentFactIndex, isPaused, completedFacts }) => {
  // Detect mobile
  const [isMobile, setIsMobile] = useState(false);
  
  // Grid settings based on device
  const GRID_SIZE = isMobile ? 18 : 20;
  const CELL_SIZE = isMobile ? 16 : 25;
  const CANVAS_SIZE = GRID_SIZE * CELL_SIZE;
  const SPEED = isMobile ? 220 : 200;
  
  const canvasRef = useRef(null);
  const [snake, setSnake] = useState([{ x: Math.floor(GRID_SIZE / 2), y: Math.floor(GRID_SIZE / 2) }]);
  const [food, setFood] = useState({ x: Math.floor(GRID_SIZE / 2) + 5, y: Math.floor(GRID_SIZE / 2) });
  const [direction, setDirection] = useState({ x: 1, y: 0 });
  const [isAutopilot, setIsAutopilot] = useState(true);
  const [manualStepsRemaining, setManualStepsRemaining] = useState(0);
  
  const directionRef = useRef({ x: 1, y: 0 });
  const autopilotRef = useRef(true);
  const manualStepsRef = useRef(0);
  const animationFrameRef = useRef(null);
  const lastUpdateTimeRef = useRef(0);
  const touchStartRef = useRef(null);

  const currentColor = facts[currentFactIndex]?.color || '#00FFB2';
  const nextColor = facts[currentFactIndex + 1]?.color || currentColor;

  // Detect mobile on mount
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // Initialize audio on first user interaction
    const initAudio = () => {
      gameSounds.init();
      document.removeEventListener('click', initAudio);
      document.removeEventListener('keydown', initAudio);
      document.removeEventListener('touchstart', initAudio);
    };
    document.addEventListener('click', initAudio);
    document.addEventListener('keydown', initAudio);
    document.addEventListener('touchstart', initAudio);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      document.removeEventListener('click', initAudio);
      document.removeEventListener('keydown', initAudio);
      document.removeEventListener('touchstart', initAudio);
    };
  }, []);

  // Generate random food position
  const generateFood = useCallback((currentSnake) => {
    let newFood;
    let isOnSnake;
    
    do {
      newFood = {
        x: Math.floor(Math.random() * GRID_SIZE),
        y: Math.floor(Math.random() * GRID_SIZE),
      };
      isOnSnake = currentSnake.some(
        (segment) => segment.x === newFood.x && segment.y === newFood.y
      );
    } while (isOnSnake);
    
    return newFood;
  }, [GRID_SIZE]);

  // Handle manual control
  const handleManualControl = useCallback((dir) => {
    const currentDir = directionRef.current;
    let newDir = null;

    switch (dir) {
      case 'up':
        if (currentDir.y === 0) newDir = { x: 0, y: -1 };
        break;
      case 'down':
        if (currentDir.y === 0) newDir = { x: 0, y: 1 };
        break;
      case 'left':
        if (currentDir.x === 0) newDir = { x: -1, y: 0 };
        break;
      case 'right':
        if (currentDir.x === 0) newDir = { x: 1, y: 0 };
        break;
      default:
        break;
    }

    if (newDir) {
      setDirection(newDir);
      directionRef.current = newDir;
      setIsAutopilot(false);
      autopilotRef.current = false;
      setManualStepsRemaining(5); // Return to autopilot after 5 steps
      manualStepsRef.current = 5;
    }
  }, []);

  // Keyboard controls
  useEffect(() => {
    if (isPaused || isMobile) return;

    const handleKeyPress = (e) => {
      const key = e.key.toLowerCase();
      
      switch (key) {
        case 'arrowup':
        case 'w':
          handleManualControl('up');
          break;
        case 'arrowdown':
        case 's':
          handleManualControl('down');
          break;
        case 'arrowleft':
        case 'a':
          handleManualControl('left');
          break;
        case 'arrowright':
        case 'd':
          handleManualControl('right');
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isPaused, isMobile, handleManualControl]);

  // Touch/swipe controls
  useEffect(() => {
    if (isPaused) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleTouchStart = (e) => {
      const touch = e.touches[0];
      touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    };

    const handleTouchEnd = (e) => {
      if (!touchStartRef.current) return;
      
      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartRef.current.x;
      const deltaY = touch.clientY - touchStartRef.current.y;
      
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);
      
      // Minimum swipe distance
      if (absDeltaX < 30 && absDeltaY < 30) return;
      
      if (absDeltaX > absDeltaY) {
        handleManualControl(deltaX > 0 ? 'right' : 'left');
      } else {
        handleManualControl(deltaY > 0 ? 'down' : 'up');
      }
      
      touchStartRef.current = null;
    };

    canvas.addEventListener('touchstart', handleTouchStart);
    canvas.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isPaused, handleManualControl]);

  // Game loop with requestAnimationFrame
  useEffect(() => {
    if (isPaused) return;

    const gameLoop = (timestamp) => {
      if (!lastUpdateTimeRef.current) {
        lastUpdateTimeRef.current = timestamp;
      }

      const deltaTime = timestamp - lastUpdateTimeRef.current;

      if (deltaTime >= SPEED) {
        lastUpdateTimeRef.current = timestamp;

        setSnake((prevSnake) => {
          const head = prevSnake[0];
          
          // Get next direction (autopilot or manual)
          let nextDir;
          
          if (autopilotRef.current) {
            // Use pathfinding
            nextDir = findPath(prevSnake, food, GRID_SIZE);
            if (nextDir) {
              directionRef.current = nextDir;
            }
          } else {
            // Manual control - decrement steps
            if (manualStepsRef.current > 0) {
              manualStepsRef.current--;
              setManualStepsRemaining(manualStepsRef.current);
              
              if (manualStepsRef.current === 0) {
                setIsAutopilot(true);
                autopilotRef.current = true;
              }
            }
            nextDir = directionRef.current;
          }

          // Calculate new head position with wrapping
          const newHead = {
            x: (head.x + directionRef.current.x + GRID_SIZE) % GRID_SIZE,
            y: (head.y + directionRef.current.y + GRID_SIZE) % GRID_SIZE,
          };

          // Check if snake hits itself
          const hitSelf = prevSnake.some(
            (segment) => segment.x === newHead.x && segment.y === newHead.y
          );

          if (hitSelf) {
            // Reset snake to center
            return [{ x: Math.floor(GRID_SIZE / 2), y: Math.floor(GRID_SIZE / 2) }];
          }

          const newSnake = [newHead, ...prevSnake];

          // Check if food is eaten
          if (newHead.x === food.x && newHead.y === food.y) {
            setFood(generateFood(newSnake));
            // Play food eaten sound
            gameSounds.playFoodEaten();
            // Trigger food eaten callback outside of render
            setTimeout(() => onFoodEaten(), 0);
            return newSnake; // Grow snake
          } else {
            newSnake.pop(); // Remove tail
            return newSnake;
          }
        });
      }

      animationFrameRef.current = requestAnimationFrame(gameLoop);
    };

    animationFrameRef.current = requestAnimationFrame(gameLoop);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [food, generateFood, onFoodEaten, isPaused, SPEED, GRID_SIZE]);

  // Draw game
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    // Draw subtle grid
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.025)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= GRID_SIZE; i++) {
      ctx.beginPath();
      ctx.moveTo(i * CELL_SIZE, 0);
      ctx.lineTo(i * CELL_SIZE, CANVAS_SIZE);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(0, i * CELL_SIZE);
      ctx.lineTo(CANVAS_SIZE, i * CELL_SIZE);
      ctx.stroke();
    }

    // Draw food with pulsing glow
    const foodX = food.x * CELL_SIZE + CELL_SIZE / 2;
    const foodY = food.y * CELL_SIZE + CELL_SIZE / 2;
    
    const gradient = ctx.createRadialGradient(foodX, foodY, 0, foodX, foodY, CELL_SIZE * 1.5);
    gradient.addColorStop(0, nextColor);
    gradient.addColorStop(0.5, nextColor + '44');
    gradient.addColorStop(1, nextColor + '00');
    ctx.fillStyle = gradient;
    ctx.fillRect(food.x * CELL_SIZE - CELL_SIZE, food.y * CELL_SIZE - CELL_SIZE, CELL_SIZE * 3, CELL_SIZE * 3);

    ctx.fillStyle = nextColor;
    ctx.beginPath();
    ctx.arc(foodX, foodY, CELL_SIZE / 3, 0, Math.PI * 2);
    ctx.fill();

    // Draw snake
    snake.forEach((segment, index) => {
      const x = segment.x * CELL_SIZE;
      const y = segment.y * CELL_SIZE;

      if (index === 0) {
        // Snake head
        ctx.fillStyle = currentColor;
        ctx.fillRect(x + 2, y + 2, CELL_SIZE - 4, CELL_SIZE - 4);
        
        const headGradient = ctx.createRadialGradient(
          x + CELL_SIZE / 2,
          y + CELL_SIZE / 2,
          0,
          x + CELL_SIZE / 2,
          y + CELL_SIZE / 2,
          CELL_SIZE
        );
        headGradient.addColorStop(0, currentColor + '66');
        headGradient.addColorStop(1, currentColor + '00');
        ctx.fillStyle = headGradient;
        ctx.fillRect(x - CELL_SIZE / 2, y - CELL_SIZE / 2, CELL_SIZE * 2, CELL_SIZE * 2);
      } else {
        // Snake body with fade
        const opacity = Math.max(0.3, 1 - index * 0.03);
        ctx.fillStyle = currentColor + Math.floor(opacity * 255).toString(16).padStart(2, '0');
        ctx.fillRect(x + 3, y + 3, CELL_SIZE - 6, CELL_SIZE - 6);
      }
    });
  }, [snake, food, currentColor, nextColor, GRID_SIZE, CELL_SIZE, CANVAS_SIZE]);

  return (
    <div className="snake-game-container">
      <div className="game-wrapper">
        <canvas
          ref={canvasRef}
          width={CANVAS_SIZE}
          height={CANVAS_SIZE}
          className="game-canvas"
        />
        
        <ProgressBar 
          completedFacts={completedFacts}
          currentFactIndex={currentFactIndex}
        />
        
        <div className="game-status">
          <p className="current-level" style={{ color: currentColor }}>
            {facts[currentFactIndex]?.level} / {facts[currentFactIndex]?.title}
          </p>
          {!isAutopilot && (
            <p className="manual-mode" style={{ color: currentColor }}>
              Manual control • {manualStepsRemaining} steps remaining
            </p>
          )}
          {isAutopilot && (
            <p className="autopilot-mode">
              {isMobile ? 'Tap d-pad or swipe to take control' : 'Press arrow keys to take control'}
            </p>
          )}
        </div>
      </div>
      
      {isMobile && (
        <MobileDPad onDirectionPress={handleManualControl} />
      )}
    </div>
  );
};

export default SnakeGameAutopilot;
